/**
 * LoanCounterpartyCard — the loan's investor / counterparty, styled as a standard
 * Loan Info card (TitledCard, like Loan Meta / Loan Terms) with the same 2-column
 * label|value data row the other cards use. Saved SEPARATELY from the shared
 * changes-form, through `POST /underwriting/add_loan_counterparty/{dealId}`
 * (useUnderwritingHook.addLoanCounterparty) — refreshes the loan from the
 * response. Free-text — see the note in LoanDrilldownDetailsTab about whether to
 * source this from a counterparty list.
 */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Tooltip from "@mui/material/Tooltip";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import HandshakeOutlined from "@mui/icons-material/HandshakeOutlined";

import TitledCard from "../../../../components/ui/TitledCard";
import Txt from "../../../../components/typography/Txt";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { blueGrey, grey, yellow } from "@mui/material/colors";
import { Divider } from "@mui/material";

// Mirrors the editable-data-row look used by RffDataRow (label cell + tinted
// value cell, 2 equal columns, dashed separators).
const SEP = "#dde0e4";
const ROW_SX = {
  display: "grid",
  gridTemplateColumns: "1fr",
  borderTop: `1px dashed ${SEP}`,
  borderBottom: `1px dashed ${SEP}`,
};
const LABEL_SX = { p: 0.8, display: "flex", alignItems: "center" };
const VALUE_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  px: 0.8,
  py: 0.5,
  background: "#e3f2fd35",
  border: "1px solid #e3f2fdff",
  borderTop: "none",
  borderBottom: "none",
};

export default function LoanCounterpartyCard() {
  const dispatch = useDispatch();
  const loanId = useSelector((s) => s.loanDrilldown?._id);
  const current =
    useSelector((s) => s.loanDrilldown?.investorCounterparty) || "";
  const { addLoanCounterparty } = useUnderwritingHook();

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setValue(current);
  }, [current, editing]);

  const handleSave = () => {
    setSaving(true);
    addLoanCounterparty({
      loanId,
      investorCounterparty: value.trim(),
      onSuccessFn: (loan) => {
        if (loan) dispatch(loanDrilldownSet(loan));
        setEditing(false);
      },
      onCompleteFn: () => setSaving(false),
      onFailFn: () => setSaving(false),
    });
  };

  return (
    <TitledCard
      variant="h3"
      title="Investor / Counterparty"
      icon={<HandshakeOutlined className="thin" />}
      fw
      // headerEndContent={
      //   editing ? (
      //     <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
      //       <Tooltip title="Cancel">
      //         <IconButton size="small" onClick={() => setEditing(false)} aria-label="Cancel">
      //           <CloseOutlined sx={{ fontSize: 17, color: "#64748b" }} />
      //         </IconButton>
      //       </Tooltip>
      //       <Tooltip title="Save">
      //         <span>
      //           <IconButton size="small" onClick={handleSave} disabled={saving} aria-label="Save">
      //             {saving ? (
      //               <CircularProgress size={15} color="inherit" />
      //             ) : (
      //               <CheckOutlined sx={{ fontSize: 17, color: "#0b8f1a" }} />
      //             )}
      //           </IconButton>
      //         </span>
      //       </Tooltip>
      //     </Box>
      //   ) : (
      //     <Tooltip title="Edit counterparty">
      //       <IconButton size="small" onClick={() => setEditing(true)} aria-label="Edit counterparty">
      //         <EditOutlined sx={{ fontSize: 17, color: "#64748b" }} />
      //       </IconButton>
      //     </Tooltip>
      //   )
      // }
    >
      <Box sx={ROW_SX}>
        {/* <Box sx={LABEL_SX}>
          <Txt sx={{ color: "#33475b", fontSize: 12 }}>
            Investor/Counterparty Name
          </Txt>
        </Box> */}
        <Box
          sx={{
            ...VALUE_SX,
            //
            justifyContent: "space-between",
            // justifyContent: editing ? "stretch" : "flex-end",
          }}
        >
          {editing ? (
            <InputBase
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Investor / counterparty name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") setEditing(false);
              }}
              sx={{
                // width: "100%",
                fontSize: 12,
                fontWeight: 600,
                "& input": { p: 0.4 },
              }}
            />
          ) : (
            <Txt
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: current ? "#0f172a" : "#94a3b8",
                textAlign: "right",
              }}
            >
              {current || "Not set"}
            </Txt>
          )}
          {editing ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                background: yellow[100],
                border: `1px solid ${yellow[400]}`,
                // padding: "4px 8px",
                borderRadius: "4px",
              }}
            >
              <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  onClick={() => setEditing(false)}
                  aria-label="Cancel"
                >
                  <CloseOutlined sx={{ fontSize: 17, color: "#424850" }} />
                </IconButton>
              </Tooltip>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: yellow[400] }}
              />
              <Tooltip title="Save">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleSave}
                    disabled={saving}
                    aria-label="Save"
                  >
                    {saving ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : (
                      <CheckOutlined sx={{ fontSize: 17, color: "#069d18" }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ) : (
            <Tooltip title="Edit counterparty">
              <IconButton
                size="small"
                onClick={() => setEditing(true)}
                aria-label="Edit counterparty"
              >
                <EditOutlined sx={{ fontSize: 17, color: "#64748b" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </TitledCard>
  );
}
